/**
 * App.jsx - Main entry point for Talk Australia Prospect Portal
 * Handles routing between public portal, admin dashboard, and section configurator
 */

import React, { useEffect, useState } from 'react';
import ProspectPortalPage from './components/portal/ProspectPortalPage';
import SectionConfigurator from './components/configurator/SectionConfigurator';

// Admin components
import AdminLogin from './components/admin/AdminLogin';
import AdminDashboardNew from './components/admin/AdminDashboardNew';
import AdminProspects from './components/admin/AdminProspects';
import AdminClients from './components/admin/AdminClients';
import AdminUsers from './components/admin/AdminUsers';
import AdminActivity from './components/admin/AdminActivity';
import AdminTickets from './components/admin/AdminTickets';
import AdminVendors from './components/admin/AdminVendors';
import AdminSettings from './components/admin/AdminSettings';
import AdminAnalytics from './components/admin/AdminAnalytics';

// Client components
import ClientLogin from './components/client/ClientLogin';
import ClientDashboard from './components/client/ClientDashboard';
import ClientOnboarding from './components/client/ClientOnboarding';
import ClientServices from './components/client/ClientServices';
import ClientTickets from './components/client/ClientTickets';
import ClientInvoices from './components/client/ClientInvoices';
import ClientDocuments from './components/client/ClientDocuments';
import ClientContracts from './components/client/ClientContracts';
import ClientProfile from './components/client/ClientProfile';
import ClientNotifications from './components/client/ClientNotifications';
import KnowledgeBase from './components/client/KnowledgeBase';
import ClientStore from './components/client/ClientStore';
import ClientBilling from './components/client/ClientBilling';
import ClientCallUs from './components/client/ClientCallUs';
import KnowledgeBaseNew from './components/client/KnowledgeBaseNew';
import ClientNotificationsCenter from './components/client/ClientNotificationsCenter';
import ClientDeviceConsent from './components/client/ClientDeviceConsent';

// Public legal pages
import LegalPage from './components/legal/LegalPage';

const styles = {
  container: {
    minHeight: '100vh',
    backgroundColor: '#0F1729',
    color: '#FFFFFF',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  },
};

/**
 * Simple hash-based router
 * Routes:
 *   /portal/:token   - Public prospect portal (no auth)
 *   /admin            - Admin dashboard (auth required)
 *   /admin/configure/:id - Section configurator for a prospect
 *   /                 - Redirects based on context
 */
function parseRoute() {
  const path = window.location.pathname;
  const params = new URLSearchParams(window.location.search);

  // /portal/:token
  const portalMatch = path.match(/^\/portal\/([a-f0-9-]+)$/i);
  if (portalMatch) {
    return { view: 'portal', token: portalMatch[1] };
  }

  // Legacy: ?token=xxx
  const tokenParam = params.get('token');
  if (tokenParam) {
    return { view: 'portal', token: tokenParam };
  }

  // /admin/configure/:id
  const configMatch = path.match(/^\/admin\/configure\/(\d+)$/);
  if (configMatch) {
    return { view: 'configurator', prospectId: configMatch[1] };
  }

  // Public legal pages
  if (path === '/privacy') return { view: 'privacy' };
  if (path === '/terms') return { view: 'terms' };

  // Admin portal routes
  if (path === '/admin/login') return { view: 'admin-login' };
  if (path === '/admin/prospects') return { view: 'admin-prospects' };
  if (path === '/admin/clients') return { view: 'admin-clients' };
  if (path.match(/^\/admin\/clients\/.+/)) return { view: 'admin-clients' };
  if (path === '/admin/tickets') return { view: 'admin-tickets' };
  if (path === '/admin/users') return { view: 'admin-users' };
  if (path === '/admin/activity') return { view: 'admin-activity' };
  if (path === '/admin/vendors') return { view: 'admin-vendors' };
  if (path === '/admin/settings') return { view: 'admin-settings' };
  if (path === '/admin/analytics') return { view: 'admin-analytics' };
  if (path === '/admin/dashboard') return { view: 'admin-dashboard' };
  if (path.startsWith('/admin')) return { view: 'admin-dashboard' };

  // Client portal routes
  if (path === '/client/login') return { view: 'client-login' };
  if (path === '/client/reset-password') return { view: 'client-reset-password' };
  if (path === '/client/onboarding') return { view: 'client-onboarding' };
  if (path === '/client/services') return { view: 'client-services' };
  if (path === '/client/tickets') return { view: 'client-tickets' };
  if (path === '/client/invoices') return { view: 'client-invoices' };
  if (path === '/client/documents') return { view: 'client-documents' };
  if (path === '/client/contracts') return { view: 'client-contracts' };
  if (path === '/client/profile') return { view: 'client-profile' };
  if (path === '/client/notifications') return { view: 'client-notifications' };
  if (path === '/client/knowledge-base') return { view: 'client-knowledge-base' };
  if (path === '/client/store') return { view: 'client-store' };
  if (path === '/client/billing') return { view: 'client-billing' };
  if (path === '/client/call-us') return { view: 'client-call-us' };
  if (path === '/client/notification-center') return { view: 'client-notification-center' };
  if (path === '/client/consent') return { view: 'client-consent' };
  if (path === '/client/privacy') return { view: 'client-consent' };
  if (path.startsWith('/client')) return { view: 'client-dashboard' };

  // Default
  return { view: 'portal', token: null };
}

function App() {
  const [route, setRoute] = useState(parseRoute);

  useEffect(() => {
    const handleNav = () => setRoute(parseRoute());
    window.addEventListener('popstate', handleNav);
    return () => window.removeEventListener('popstate', handleNav);
  }, []);

  // Client auth check for protected client routes
  const clientToken = typeof window !== 'undefined' ? localStorage.getItem('ta_client_token') : null;
  const isClientRoute = route.view.startsWith('client-') && route.view !== 'client-login' && route.view !== 'client-reset-password';

  if (isClientRoute && !clientToken) {
    window.location.href = '/client/login';
    return null;
  }

  // Admin auth check for protected admin routes
  const adminToken = typeof window !== 'undefined' ? localStorage.getItem('ta_admin_token') : null;
  const isAdminRoute = route.view.startsWith('admin-') && route.view !== 'admin-login';

  if (isAdminRoute && !adminToken) {
    window.location.href = '/admin/login';
    return null;
  }

  switch (route.view) {
    // Admin portal routes
    case 'admin-login':
      return <AdminLogin />;
    case 'admin-dashboard':
      return <AdminDashboardNew />;
    case 'admin-prospects':
      return <AdminProspects />;
    case 'admin-clients':
      return <AdminClients />;
    case 'admin-tickets':
      return <AdminTickets />;
    case 'admin-users':
      return <AdminUsers />;
    case 'admin-activity':
      return <AdminActivity />;
    case 'admin-vendors':
      return <AdminVendors />;
    case 'admin-settings':
      return <AdminSettings />;
    case 'admin-analytics':
      return <AdminAnalytics />;

    case 'configurator':
      return (
        <div style={styles.container}>
          <SectionConfigurator prospectId={route.prospectId} />
        </div>
      );

    // Client portal routes
    case 'client-login':
      return <ClientLogin />;
    case 'client-reset-password':
      return <ClientLogin resetMode />;
    case 'client-dashboard':
      return <ClientDashboard />;
    case 'client-onboarding':
      return <ClientOnboarding />;
    case 'client-services':
      return <ClientServices />;
    case 'client-tickets':
      return <ClientTickets />;
    case 'client-invoices':
      return <ClientInvoices />;
    case 'client-documents':
      return <ClientDocuments />;
    case 'client-contracts':
      return <ClientContracts />;
    case 'client-profile':
      return <ClientProfile />;
    case 'client-notifications':
      return <ClientNotifications />;
    case 'client-knowledge-base':
      return <KnowledgeBaseNew />;
    case 'client-store':
      return <ClientStore />;
    case 'client-billing':
      return <ClientBilling />;
    case 'client-call-us':
      return <ClientCallUs />;
    case 'client-notification-center':
      return <ClientNotificationsCenter />;
    case 'client-consent':
      return <ClientDeviceConsent />;

    // Public legal pages
    case 'privacy':
      return <LegalPage page="privacy" />;
    case 'terms':
      return <LegalPage page="terms" />;

    case 'portal':
    default:
      return (
        <div style={styles.container}>
          <ProspectPortalPage token={route.token} />
        </div>
      );
  }
}

export default App;
